import type { ReactNode } from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';

/** The three outcome treatments the magic-link approval landing renders. */
export type ApprovalOutcome = 'approved' | 'denied' | 'expired';

export interface ApprovalOutcomeCardProps {
  /** Which treatment to render (medallion colour + icon). */
  outcome: ApprovalOutcome;
  /** Card headline (already localized). */
  headline: string;
  /** Card body — plain string or rich nodes (e.g. bolded request type / name). */
  body: ReactNode;
  /** Portal deep-link href (the `view` redirect for approved/denied, `/` otherwise). */
  portalHref: string;
  /** Portal link label (already localized). */
  portalLabel: string;
  /**
   * Primary treatment renders a full-width filled button (the expired state's
   * "Open the portal"); secondary renders an inline text link with a trailing
   * arrow (the approved/denied "View request in the portal").
   */
  portalPrimary?: boolean;
}

interface MedallionSpec {
  /** Selects the medallion foreground (icon + ring tint) from the active theme. */
  fg: (t: Theme) => string;
  icon: ReactNode;
}

/**
 * Outcome medallion + headline + body + portal deep-link — the inner content of
 * the anonymous approval landing (rendered inside {@link PublicLayout}).
 * Presentational: all copy is passed in already-localized; the outcome only
 * selects the medallion treatment. Ported from the prototype `Approval Magic
 * Link` `Medallion` + `Card` + `PortalLink`.
 *
 * A denial carries no captured reason here: per the block-084 PR review, declining
 * needs no reason — the approver may optionally add one as a note on the request
 * detail page the portal link deep-links to (the body invites this). See the
 * resolved TD-255.
 */
export function ApprovalOutcomeCard({
  outcome,
  headline,
  body,
  portalHref,
  portalLabel,
  portalPrimary = false,
}: ApprovalOutcomeCardProps) {
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: '20px' }}>
        <Medallion outcome={outcome} />
      </Box>

      <Typography
        variant="h5"
        component="h1"
        sx={{ fontWeight: 700, fontSize: 22, lineHeight: 1.3, color: 'text.primary', mb: '12px' }}
      >
        {headline}
      </Typography>

      <Typography
        variant="body1"
        component="p"
        sx={{
          maxWidth: 320,
          mx: 'auto',
          fontSize: 14.5,
          lineHeight: 1.65,
          color: 'text.secondary',
        }}
      >
        {body}
      </Typography>

      <Box sx={{ mt: '26px' }}>
        {portalPrimary ? (
          <Button href={portalHref} variant="contained" fullWidth sx={{ minHeight: 46 }}>
            {portalLabel}
          </Button>
        ) : (
          <Link
            href={portalHref}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: 13.5,
              fontWeight: 600,
              color: 'primary.main',
              textDecoration: 'none',
            }}
          >
            {portalLabel}
            <Box component="span" aria-hidden="true">
              →
            </Box>
          </Link>
        )}
      </Box>
    </>
  );
}

const MEDALLIONS: Record<ApprovalOutcome, MedallionSpec> = {
  approved: {
    fg: (t) => t.palette.success.main,
    icon: <CheckRounded />,
  },
  denied: {
    fg: (t) => t.palette.text.secondary,
    icon: <CloseRounded />,
  },
  expired: {
    fg: (t) => t.palette.text.disabled,
    icon: <AccessTimeRounded />,
  },
};

function Medallion({ outcome }: { outcome: ApprovalOutcome }) {
  const spec = MEDALLIONS[outcome];
  return (
    <Box
      sx={(t) => {
        const fg = spec.fg(t);
        return {
          width: 68,
          height: 68,
          borderRadius: '50%',
          bgcolor: alpha(fg, 0.12),
          border: '2px solid',
          borderColor: alpha(fg, 0.28),
          display: 'grid',
          placeItems: 'center',
          color: fg,
          '& svg': { fontSize: 32 },
        };
      }}
    >
      {spec.icon}
    </Box>
  );
}
